import { useState } from 'react';
import { Link } from 'react-router-dom';
import { resetPassword } from 'aws-amplify/auth';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await resetPassword({ username: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (err) {
      // Don't reveal whether the email exists (user enumeration)
      // Cognito may return LimitExceededException if too many requests
      const message = err instanceof Error ? err.message : '';
      if (message.includes('LimitExceeded') || message.includes('TooManyRequests')) {
        setError('Too many attempts. Please wait a few minutes and try again.');
      } else {
        // For all other errors (including user-not-found), show the generic success UI
        // to avoid revealing whether this email has an account
        setSubmitted(true);
      }
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <AuthLayout>
        <div className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center">
            <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Check your email</h1>
          <p className="text-sm text-gray-600 mb-8">
            If <strong>{email}</strong> has an account, you'll receive a reset code shortly.
          </p>
          <Link
            to={`/reset-password?email=${encodeURIComponent(email)}`}
            className="inline-block w-full px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors text-center"
          >
            Enter reset code
          </Link>
          <Link
            to="/login"
            className="block mt-4 text-sm text-gray-600 hover:text-gray-900"
          >
            Back to sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h1 className="text-2xl font-semibold text-gray-900 mb-2">Reset password</h1>
      <p className="text-sm text-gray-600 mb-8">
        Enter your email and we'll send you a reset code.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Sending…' : 'Send reset code'}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-gray-600">
        <Link to="/login" className="text-indigo-600 hover:text-indigo-700">
          Back to sign in
        </Link>
      </p>
    </AuthLayout>
  );
}

function AuthLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      <div className="mb-8">
        <span className="text-xl font-bold text-gray-900 tracking-tight">Slingshotio</span>
      </div>
      <div className="w-full max-w-sm bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        {children}
      </div>
    </div>
  );
}
